/**
 * View-independent visibility, swept on the GPU: ambient occlusion, a bent
 * normal, lateral support for translucency, and one bounce of coloured light.
 *
 * What this replaces was a difference of Gaussians — `blur(height, sigma 7)`
 * minus the height, scaled and clamped. That is a measure of how far below its
 * own neighbourhood a point sits, which is not occlusion: it has no notion of
 * direction, so it cannot tell a narrow canyon from a wide bowl, and it cannot
 * say which part of the sky a point can actually see. On a relief whose subject
 * is millions of closely packed spires, that difference is most of the picture.
 *
 * Here every texel searches its horizon along twenty azimuths, marching outwards
 * with a geometric stride and reading progressively coarser levels of the
 * max-preserving height chain — so a distant thin spire still occludes, which a
 * point sample would miss entirely. Four useful quantities fall out of the same
 * search, which is the reason to do it this way rather than four ways:
 *
 *  - **Occlusion.** Integrating `cos a sin a` from the horizon angle to the
 *    zenith gives exactly `cos^2 = 1 / (1 + tan^2)` per slice, so the estimator is
 *    analytic rather than tuned.
 *  - **Bent normal.** The centroid of that same visible wedge, also analytic. In a
 *    trench between spires the visible sky is nowhere near the geometric normal,
 *    and looking up the environment along the normal anyway is what makes a dense
 *    relief read as a flat print of itself.
 *  - **Lateral support.** Whether material continues sideways a few cells away.
 *    Flat ground is supported and a spire tip is not, which is precisely the
 *    distinction gypsum's translucency needs.
 *  - **One bounce.** Whatever closed the horizon is also what reflects light back
 *    into it, so the occluder's albedo is already in hand.
 */

import * as THREE from "three";

import { BOUNCE_STRENGTH, TRANSLUCENCY_DEPTH } from "../../shared/constants.ts";
import type { ReliefFields } from "../../shared/types.ts";
import type { ReliefPalettes } from "./palette.ts";

/**
 * Occlusion is low-frequency next to the geometry that casts it, so half the
 * field's resolution is plenty and it quarters a bake that runs on every preview.
 */
const BAKE_DIVISOR = 2;

const fragmentShader = /* glsl */ `
precision highp float;
precision highp sampler2D;

uniform sampler2D uHeight;
uniform sampler2D uShade;
uniform sampler2D uTerrainPalette;
uniform sampler2D uPopulationPalette;
uniform vec2 uResolution;
uniform vec2 uFieldSize;
uniform vec2 uCellSize;
uniform float uBaseLod;
uniform float uMaxLod;
uniform float uThicknessDepth;
uniform float uBounceStrength;

layout(location = 0) out vec4 outVisibility;
layout(location = 1) out vec4 outBounce;

const int AZIMUTHS = 20;
const int STEPS = 14;
const float START_CELLS = 1.5;
const float GROWTH = 1.45;
const float SUPPORT_CELLS = 3.0;
const float TAU = 6.283185307179586;

vec3 albedoAt(vec2 at, float lod) {
  vec4 shade = textureLod(uShade, at, lod);
  float population = clamp(shade.r, 0.0, 1.0);
  vec3 stone = texture2D(uTerrainPalette, vec2(clamp(shade.g, 0.0, 1.0), 0.5)).rgb;
  vec3 ink = texture2D(uPopulationPalette, vec2(population, 0.5)).rgb;
  return mix(stone, ink, smoothstep(0.008, 0.2, population));
}

void main() {
  vec2 grid = gl_FragCoord.xy / uResolution;
  float centre = textureLod(uHeight, grid, uBaseLod).r;

  float visible = 0.0;
  vec3 bent = vec3(0.0);
  vec3 bounce = vec3(0.0);
  float support = 0.0;

  for (int slice = 0; slice < AZIMUTHS; slice++) {
    float angle = (float(slice) + 0.5) * (TAU / float(AZIMUTHS));
    vec2 direction = vec2(cos(angle), sin(angle));
    vec2 stride = direction / uFieldSize;
    float worldPerCell =
      length(vec2(direction.x * uCellSize.x, direction.y * uCellSize.y));

    // Does the material carry on sideways? Flat ground says yes, a spire says no.
    float beside = textureLod(uHeight, grid + stride * SUPPORT_CELLS, uBaseLod).r;
    support += clamp((beside - centre + uThicknessDepth) / uThicknessDepth, 0.0, 1.0);

    // Highest horizon along this azimuth. Zero is the floor on purpose: a
    // direction that falls away is fully visible within the upper hemisphere, not
    // more than fully visible.
    float highest = 0.0;
    vec2 occluder = grid;
    float reach = START_CELLS;
    for (int step = 0; step < STEPS; step++) {
      float lod = clamp(uBaseLod + log2(reach) - 1.0, uBaseLod, uMaxLod);
      vec2 at = grid + stride * reach;
      float tangent = (textureLod(uHeight, at, lod).r - centre) / (reach * worldPerCell);
      if (tangent > highest) {
        highest = tangent;
        occluder = at;
      }
      reach *= GROWTH;
    }

    float sliceVisible = 1.0 / (1.0 + highest * highest);
    visible += sliceVisible;

    float cosHorizon = sqrt(sliceVisible);
    float sinHorizon = sqrt(max(1.0 - sliceVisible, 0.0));
    // The common factor of a third cancels in the normalisation below.
    bent += vec3(
      direction * (cosHorizon * cosHorizon * cosHorizon),
      1.0 - sinHorizon * sinHorizon * sinHorizon
    );

    bounce += (1.0 - sliceVisible) * albedoAt(occluder, uBaseLod + 2.0);
  }

  float inverse = 1.0 / float(AZIMUTHS);
  // Fully enclosed leaves no wedge at all, and normalising nothing is a NaN that
  // would spread through the environment lookup.
  vec3 bentNormal = normalize(bent + vec3(0.0, 0.0, 1e-5));
  outVisibility = vec4(
    clamp(visible * inverse, 0.0, 1.0),
    bentNormal.x,
    bentNormal.y,
    clamp(support * inverse, 0.0, 1.0)
  );
  outBounce = vec4(bounce * (inverse * uBounceStrength), 1.0);
}
`;

const vertexShader = /* glsl */ `
void main() {
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

export interface BakedVisibility {
  /** RGBA16F: occlusion, bent normal xy, lateral support. */
  visibility: THREE.Texture;
  /** RGBA16F: one bounce of indirect colour. */
  bounce: THREE.Texture;
  dispose(): void;
}

/**
 * The sweep is one full-screen pass, so its scene, camera and program are built
 * once and reused. Rebuilding them per bake would recompile the shader on every
 * preview frame.
 */
interface Pass {
  scene: THREE.Scene;
  camera: THREE.Camera;
  material: THREE.ShaderMaterial;
}

let pass: Pass | null = null;

function getPass(palettes: ReliefPalettes): Pass {
  if (pass) return pass;
  const material = new THREE.ShaderMaterial({
    glslVersion: THREE.GLSL3,
    uniforms: {
      uHeight: { value: null },
      uShade: { value: null },
      uTerrainPalette: { value: palettes.terrain },
      uPopulationPalette: { value: palettes.population },
      uResolution: { value: new THREE.Vector2() },
      uFieldSize: { value: new THREE.Vector2() },
      uCellSize: { value: new THREE.Vector2() },
      uBaseLod: { value: 1 },
      uMaxLod: { value: 1 },
      uThicknessDepth: { value: TRANSLUCENCY_DEPTH },
      uBounceStrength: { value: BOUNCE_STRENGTH },
    },
    vertexShader,
    fragmentShader,
    depthTest: false,
    depthWrite: false,
  });
  const scene = new THREE.Scene();
  scene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material));
  pass = { scene, camera: new THREE.Camera(), material };
  return pass;
}

export function bakeVisibility(
  renderer: THREE.WebGLRenderer,
  palettes: ReliefPalettes,
  fields: ReliefFields,
  height: THREE.Texture,
  shade: THREE.Texture,
): BakedVisibility {
  const width = Math.max(1, Math.floor(fields.width / BAKE_DIVISOR));
  const rows = Math.max(1, Math.floor(fields.height / BAKE_DIVISOR));

  const target = new THREE.WebGLRenderTarget(width, rows, {
    count: 2,
    type: THREE.HalfFloatType,
    format: THREE.RGBAFormat,
    minFilter: THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
    depthBuffer: false,
    stencilBuffer: false,
    generateMipmaps: false,
  });
  for (const texture of target.textures) {
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.colorSpace = THREE.NoColorSpace;
  }

  const { scene, camera, material } = getPass(palettes);
  const uniforms = material.uniforms;
  uniforms.uHeight.value = height;
  uniforms.uShade.value = shade;
  (uniforms.uResolution.value as THREE.Vector2).set(width, rows);
  (uniforms.uFieldSize.value as THREE.Vector2).set(fields.width, fields.height);
  (uniforms.uCellSize.value as THREE.Vector2).set(fields.cellX, fields.cellY);
  // The bake runs at half the field, so its own texel is one level down, and the
  // march may climb to the coarsest level the chain provides.
  uniforms.uBaseLod.value = Math.log2(BAKE_DIVISOR);
  uniforms.uMaxLod.value = fields.heightMips.length - 1;

  const previousTarget = renderer.getRenderTarget();
  renderer.setRenderTarget(target);
  renderer.render(scene, camera);
  renderer.setRenderTarget(previousTarget);

  return {
    visibility: target.textures[0],
    bounce: target.textures[1],
    dispose(): void {
      target.dispose();
    },
  };
}
